import React, { forwardRef } from "react";
import { Scissors, Truck } from "lucide-react";
import { COMPANY } from "../lib/company";
import {
  orderDiscount,
  orderExtraCharge,
  calculateItemTax,
  calculateCodCharge,
  getInvoiceNo,
  orderAdvanceAmount,
  orderBalanceDue,
  numberToIndianWords,
  isWestBengalState
} from "../lib/orderHelpers";

/**
 * Reusable A4 Invoice & Shipping Label Sheet.
 * Respects strict 1-page A4 format for both on-screen printing and PDF exports.
 * If GST is present and order is GST-enabled, includes GSTIN, HSN codes, and itemized tax breakdowns:
 *   - West Bengal (Intra-State) -> CGST 50% + SGST 50%
 *   - Outside West Bengal (Inter-State) -> IGST 100%
 * If Non-GST order, omits all tax details, rates, and company GSTIN.
 */
const InvoiceSheet = forwardRef(function InvoiceSheet(
  {
    order,
    products = [],
    company = COMPANY,
    gstMode = order?.gstMode || "inclusive",
    breakdownCgstSgst: propBreakdownCgstSgst,
    includeTotalValueOnLabel = false,
  },
  ref
) {
  if (!order) return null;

  const created = new Date(
    order.createdAt?.seconds ? order.createdAt.seconds * 1000 : order.createdAt
  );
  const invoiceNo = getInvoiceNo(order);

  // Check if order has GST enabled or positive tax
  const hasOrderGst =
    order.isGst === true ||
    order.isGstOrder === true ||
    Number(order.tax || 0) > 0 ||
    Number(order.taxTotal || 0) > 0;

  // Determine if this is a Non-GST invoice
  const isExplicitNonGst =
    (gstMode === "none" || gstMode === "non-gst") ||
    (!hasOrderGst &&
      (order.isNonGst === true ||
        order.nonGst === true ||
        order.isGstOrder === false ||
        order.gstType === "non-gst" ||
        order.gstMode === "none" ||
        order.gstMode === "non-gst"));

  const hasGST = !isExplicitNonGst;
  const isExclusive = hasGST && gstMode === "exclusive";

  // Check state: West Bengal (Intra-State: CGST + SGST) vs Outside West Bengal (Inter-State: IGST)
  const orderState = order.state || "West Bengal";
  const isIntraState = isWestBengalState(orderState);

  const storedTax = Number(order.taxTotal != null && order.taxTotal !== "" ? order.taxTotal : (order.tax || 0));
  const storedSubtotal = Number(order.subtotal || 0);

  // Compute implied tax rate from order if items don't specify it
  const impliedOrderTaxRate = storedTax > 0 && storedSubtotal > 0
    ? Number(((storedTax / storedSubtotal) * 100).toFixed(2))
    : (order.gstRate != null ? Number(order.gstRate) : 0);

  const itemsWithTax = (order.items || []).map((it) => {
    const prod = products.find((p) => p.id === it.id);
    let itemRate = 0;
    if (it.taxRate != null && it.taxRate !== "" && !isNaN(Number(it.taxRate)) && Number(it.taxRate) > 0) {
      itemRate = Number(it.taxRate);
    } else if (it.gstRate != null && it.gstRate !== "" && !isNaN(Number(it.gstRate)) && Number(it.gstRate) > 0) {
      itemRate = Number(it.gstRate);
    } else if (prod?.gstRate != null && prod.gstRate !== "" && !isNaN(Number(prod.gstRate)) && Number(prod.gstRate) > 0) {
      itemRate = Number(prod.gstRate);
    } else if (prod?.taxRate != null && prod.taxRate !== "" && !isNaN(Number(prod.taxRate)) && Number(prod.taxRate) > 0) {
      itemRate = Number(prod.taxRate);
    } else if (impliedOrderTaxRate > 0) {
      itemRate = impliedOrderTaxRate;
    } else if (hasGST && storedTax > 0) {
      itemRate = 5;
    }

    const rate = hasGST ? itemRate : 0;
    const itemInclusive = order.priceIncludesGst !== false && !isExclusive;
    const tax = calculateItemTax(it.price, it.quantity, rate, itemInclusive);

    if (it.taxAmount != null && !isNaN(Number(it.taxAmount)) && Number(it.taxAmount) > 0) {
      tax.taxAmount = Number(it.taxAmount);
      if (itemInclusive) {
        tax.exclTax = Number(((Number(it.price) * Number(it.quantity || 1)) - tax.taxAmount).toFixed(2));
        tax.totalInclTax = Number((Number(it.price) * Number(it.quantity || 1)).toFixed(2));
      } else {
        tax.exclTax = Number((Number(it.price) * Number(it.quantity || 1)).toFixed(2));
        tax.totalInclTax = Number((tax.exclTax + tax.taxAmount).toFixed(2));
      }
    }

    return { ...it, tax, rate };
  });

  let totalExclTax = itemsWithTax.reduce((s, it) => s + it.tax.exclTax, 0);
  let totalTaxAmount = hasGST ? itemsWithTax.reduce((s, it) => s + it.tax.taxAmount, 0) : 0;

  // Fallback to stored order tax if items produced 0 tax or less
  if (hasGST && storedTax > 0 && totalTaxAmount === 0) {
    totalTaxAmount = storedTax;
    if (storedSubtotal > 0) {
      totalExclTax = storedSubtotal;
    }
  }

  const isDeliveryIncluded = order.deliveryIncluded !== false;

  // Product-based shipping total for invoice
  const productShippingTotal = (order.items || []).reduce((sum, it) => {
    const prod = products.find((p) => p.id === it.id);
    const shipUnit =
      it.shippingCharge != null && it.shippingCharge !== ""
        ? Number(it.shippingCharge)
        : Number(prod?.shippingCharge || 0);
    return sum + shipUnit * Number(it.quantity || 1);
  }, 0);

  const unincludedShipping =
    Number(order.shippingCharge || 0) > 0
      ? Number(order.shippingCharge)
      : productShippingTotal;
  const invoiceShipping = isDeliveryIncluded ? 0 : unincludedShipping;

  const itemsSubtotal = (order.items || []).reduce(
    (s, it) => s + Number(it.price || 0) * Number(it.quantity || 1),
    0
  );

  // Total items value including tax
  const itemsGross = hasGST
    ? (totalExclTax + totalTaxAmount)
    : itemsSubtotal;

  // Total bill before COD fee: items (incl tax) + shipping (if not included) + extra - discount
  const billBeforeCod = Math.max(
    0,
    itemsGross +
      invoiceShipping +
      orderExtraCharge(order) -
      orderDiscount(order)
  );

  const isCodOrder =
    Number(order.codCharge || 0) > 0 ||
    order.paymentType === "COD" ||
    (typeof order.paymentMethod === "string" && order.paymentMethod.toUpperCase().includes("COD"));

  // COD Handling Charge: ₹50 or 3% of total bill, whichever is higher
  const codHandlingFee = isCodOrder
    ? (order.codCharge != null && order.codCharge !== "" && !isNaN(Number(order.codCharge))
        ? Number(order.codCharge)
        : calculateCodCharge(billBeforeCod))
    : 0;

  // Grand Total = Total Bill + COD handling cost
  const invoiceGrandTotal = Math.max(
    0,
    Math.round(billBeforeCod + codHandlingFee)
  );

  // Advance Amount: Manually filled for manual invoice generation
  const advancePaid = isCodOrder
    ? (order.advancePaid != null && order.advancePaid !== "" && !isNaN(Number(order.advancePaid))
        ? Number(order.advancePaid)
        : 0)
    : 0;

  // Collect on Delivery = (Total Bill + COD handling cost) - Advance Amount
  const balanceDue = isCodOrder
    ? Math.max(0, invoiceGrandTotal - advancePaid)
    : 0;

  // Number of leading columns before the final amount column in tfoot:
  const summaryColSpan = !hasGST ? 5 : (isIntraState ? 8 : 7);

  return (
    <div ref={ref} className="a4-sheet bg-white text-neutral-900 font-sans">
      {/* ═══ TOP HALF · INVOICE ═════════════════════════════ */}
      <section className="pr-section invoice">
        <header className="flex items-start justify-between mb-3 pb-2.5 border-b border-neutral-300">
          <div>
            <div className="text-xl font-serif font-bold tracking-tight leading-none text-neutral-900">
              {company.name}
            </div>
            <div className="text-[11px] text-neutral-600 mt-1 leading-snug">
              {company.address}, {company.city}
              {company.state ? `, ${company.state}` : ""} — {company.pincode}
              <br />
              Ph: {company.phone} · Email: {company.email}
              {hasGST && (
                <>
                  <br />
                  GSTIN: <strong className="font-mono text-neutral-800">{company.gst || "19AAACL1234A1Z5"}</strong>
                </>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="uppercase tracking-[0.25em] text-[9px] font-bold text-neutral-500">
              {hasGST ? "Tax Invoice" : "Invoice"}
            </div>
            <div className="text-xl font-serif font-bold mt-0.5 text-neutral-900">
              #{invoiceNo}
            </div>
            <div className="text-[11px] text-neutral-600 mt-0.5">
              Date:{" "}
              {created.toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </div>
            <div className="text-[11px] text-neutral-600">
              Status: <strong className="uppercase">{order.status}</strong>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-2 gap-4 mb-2.5 text-[11px]">
          <div className="bg-neutral-50 p-2.5 rounded border border-neutral-200">
            <div className="uppercase tracking-wider text-[9px] font-bold text-neutral-500 mb-1">
              Billed &amp; Shipped To
            </div>
            <div className="font-bold text-sm text-neutral-900">
              {order.customerName}
            </div>
            <div className="text-neutral-700 leading-snug mt-0.5">
              {order.address}
              <br />
              {order.city}
              {order.state ? `, ${order.state}` : ""} —{" "}
              <strong className="text-neutral-900">{order.pincode}</strong>
              <br />
              Phone: <strong>{order.phone}</strong>
              {order.email ? ` · ${order.email}` : ""}
              {hasGST && (
                <div className="text-[10px] text-neutral-600 mt-0.5 font-medium">
                  Place of Supply: <strong>{order.state || "West Bengal"}</strong>{" "}
                  <span className="text-[9px] text-neutral-500">
                    ({isIntraState ? "Intra-State • WB" : "Inter-State • IGST"})
                  </span>
                </div>
              )}
              {hasGST && (order.customerGst || order.gstin) && (
                <div className="mt-1 pt-1 border-t border-neutral-200 text-neutral-900">
                  Customer GSTIN: <strong className="font-mono font-bold text-terracotta">{order.customerGst || order.gstin}</strong>
                </div>
              )}
            </div>
          </div>
          <div className="bg-neutral-50 p-2.5 rounded border border-neutral-200">
            <div className="uppercase tracking-wider text-[9px] font-bold text-neutral-500 mb-1">
              Order Summary
            </div>
            <div className="text-neutral-700 leading-snug text-[11px]">
              Order ID:{" "}
              <strong>#{String(order.id || "").toUpperCase()}</strong>
              <br />
              Order Mode: <strong>{order.orderMode || "Online"}</strong>
              <br />
              Placed On: {created.toLocaleString("en-IN")}
              <br />
              Payment:{" "}
              <strong>
                {order.paymentMethod ||
                  (order.orderMode === "Offline"
                    ? "In-Store / Direct / UPI"
                    : "Prepaid / WhatsApp")}
              </strong>
              {(order.consignmentNumber || order.trackingNumber) && (
                <>
                  <br />
                  Consignment No:{" "}
                  <strong className="font-mono text-neutral-900">
                    {order.consignmentNumber || order.trackingNumber}
                  </strong>{" "}
                  ({order.courierName || "Others"})
                </>
              )}
            </div>
          </div>
        </div>

        <table className="w-full text-[11px] border-collapse mt-1">
          <thead>
            <tr className="border-t-2 border-b-2 border-neutral-900 text-left bg-neutral-100 font-bold">
              <th className="py-1 px-1.5">#</th>
              <th className="py-1 px-1.5">Item Description</th>
              <th className="py-1 px-1.5 text-center">HSN/SAC</th>
              <th className="py-1 px-1.5 text-right">Qty</th>
              <th className="py-1 px-1.5 text-right">
                {isExclusive ? "Base Rate" : (hasGST ? "Unit Rate (Incl. GST)" : "Unit Rate")}
              </th>
              {hasGST && (
                <>
                  <th className="py-1 px-1.5 text-right">Taxable Val</th>
                  {isIntraState ? (
                    <>
                      <th className="py-1 px-1.5 text-right">CGST</th>
                      <th className="py-1 px-1.5 text-right">SGST</th>
                    </>
                  ) : (
                    <th className="py-1 px-1.5 text-right">IGST</th>
                  )}
                </>
              )}
              <th className="py-1 px-1.5 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {itemsWithTax.map((it, i) => {
              const itemTotal = Number(it.price || 0) * Number(it.quantity || 1);
              const halfTaxRate = Number((it.rate / 2).toFixed(2));
              const halfTaxAmt = (it.tax.taxAmount / 2).toFixed(2);
              const hsnCode = it.hsn || it.hsnCode || "—";
              return (
                <tr key={i} className="border-b border-neutral-200">
                  <td className="py-1 px-1.5 text-neutral-500">{i + 1}</td>
                  <td className="py-1 px-1.5">
                    {it.sku && (
                      <span className="font-mono text-[10px] text-neutral-700 bg-neutral-100 border border-neutral-300 px-1 py-0.5 rounded mr-1.5 font-semibold">
                        {it.sku}
                      </span>
                    )}
                    <strong className="text-neutral-900">{it.name}</strong>
                    {it.variantTag && it.variantTag !== "Default" && it.variantTag !== it.sku && (
                      <span className="text-neutral-500"> ({it.variantTag})</span>
                    )}
                  </td>
                  <td className="py-1 px-1.5 text-center font-mono text-[10px] text-neutral-700 font-semibold">
                    {hsnCode}
                  </td>
                  <td className="py-1 px-1.5 text-right font-price font-medium">
                    {it.quantity}
                  </td>
                  <td className="py-1 px-1.5 text-right font-price">₹{it.price}</td>
                  {hasGST && (
                    <>
                      <td className="py-1 px-1.5 text-right font-price">
                        ₹{it.tax.exclTax}
                      </td>
                      {isIntraState ? (
                        <>
                          <td className="py-1 px-1.5 text-right font-price leading-tight text-[10px]">
                            {halfTaxRate}% <span className="text-neutral-500 block">₹{halfTaxAmt}</span>
                          </td>
                          <td className="py-1 px-1.5 text-right font-price leading-tight text-[10px]">
                            {halfTaxRate}% <span className="text-neutral-500 block">₹{halfTaxAmt}</span>
                          </td>
                        </>
                      ) : (
                        <td className="py-1 px-1.5 text-right font-price leading-tight text-[10px]">
                          {it.rate}% <span className="text-neutral-500 block">₹{it.tax.taxAmount}</span>
                        </td>
                      )}
                    </>
                  )}
                  <td className="py-1 px-1.5 text-right font-price font-bold">
                    ₹{hasGST ? it.tax.totalInclTax : itemTotal}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            {hasGST ? (
              <>
                <tr className="border-t border-neutral-300">
                  <td
                    colSpan={summaryColSpan}
                    className="py-1 px-1.5 text-right text-neutral-600 font-medium"
                  >
                    {isExclusive ? "Taxable Subtotal (Base)" : "Taxable Subtotal (Excl. GST)"}
                  </td>
                  <td className="py-1 px-1.5 text-right font-price font-semibold">
                    ₹{totalExclTax.toFixed(2)}
                  </td>
                </tr>
                {isIntraState ? (
                  <>
                    <tr>
                      <td
                        colSpan={summaryColSpan}
                        className="py-0.5 px-1.5 text-right text-neutral-600 font-medium"
                      >
                        Central GST (CGST - 50%)
                      </td>
                      <td className="py-0.5 px-1.5 text-right font-price font-semibold text-neutral-800">
                        + ₹{(totalTaxAmount / 2).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td
                        colSpan={summaryColSpan}
                        className="py-0.5 px-1.5 text-right text-neutral-600 font-medium"
                      >
                        State GST (SGST - 50%)
                      </td>
                      <td className="py-0.5 px-1.5 text-right font-price font-semibold text-neutral-800">
                        + ₹{(totalTaxAmount / 2).toFixed(2)}
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td
                      colSpan={summaryColSpan}
                      className="py-0.5 px-1.5 text-right text-neutral-600 font-medium"
                    >
                      Integrated GST (IGST - 100%)
                    </td>
                    <td className="py-0.5 px-1.5 text-right font-price font-semibold text-neutral-800">
                      + ₹{totalTaxAmount.toFixed(2)}
                    </td>
                  </tr>
                )}
              </>
            ) : (
              <tr className="border-t border-neutral-300">
                <td
                  colSpan={summaryColSpan}
                  className="py-1 px-1.5 text-right text-neutral-600 font-medium"
                >
                  Subtotal
                </td>
                <td className="py-1 px-1.5 text-right font-price font-semibold">
                  ₹{itemsSubtotal.toFixed(2)}
                </td>
              </tr>
            )}

            {isDeliveryIncluded ? (
              <tr>
                <td
                  colSpan={summaryColSpan}
                  className="py-0.5 px-1.5 text-right text-neutral-600 font-medium"
                >
                  Shipping Charge
                </td>
                <td className="py-0.5 px-1.5 text-right font-price font-semibold text-emerald-700">
                  FREE (Delivery Included)
                </td>
              </tr>
            ) : invoiceShipping > 0 ? (
              <tr>
                <td
                  colSpan={summaryColSpan}
                  className="py-0.5 px-1.5 text-right text-neutral-600 font-medium"
                >
                  Shipping Charge
                </td>
                <td className="py-0.5 px-1.5 text-right font-price font-semibold">
                  + ₹{invoiceShipping}
                </td>
              </tr>
            ) : (
              <tr>
                <td
                  colSpan={summaryColSpan}
                  className="py-0.5 px-1.5 text-right text-neutral-600 font-medium"
                >
                  Shipping Charge
                </td>
                <td className="py-0.5 px-1.5 text-right font-sans font-semibold text-amber-700 dark:text-amber-600 italic text-[10.5px]">
                  Will be calculated later
                </td>
              </tr>
            )}
            {orderExtraCharge(order) > 0 && (
              <tr>
                <td
                  colSpan={summaryColSpan}
                  className="py-0.5 px-1.5 text-right text-neutral-600 font-medium"
                >
                  Extra Charge
                </td>
                <td className="py-0.5 px-1.5 text-right font-price font-semibold">
                  + ₹{orderExtraCharge(order)}
                </td>
              </tr>
            )}
            {orderDiscount(order) > 0 && (
              <tr>
                <td
                  colSpan={summaryColSpan}
                  className="py-0.5 px-1.5 text-right text-neutral-600 font-medium"
                >
                  Discount
                </td>
                <td className="py-0.5 px-1.5 text-right font-price text-red-700 font-semibold">
                  − ₹{orderDiscount(order)}
                </td>
              </tr>
            )}
            {codHandlingFee > 0 && (
              <tr>
                <td
                  colSpan={summaryColSpan}
                  className="py-0.5 px-1.5 text-right text-neutral-600 font-medium"
                >
                  COD Handling Charge (₹50 or 3%)
                </td>
                <td className="py-0.5 px-1.5 text-right font-price font-semibold text-amber-800">
                  + ₹{codHandlingFee}
                </td>
              </tr>
            )}
            <tr className="border-t-2 border-b border-neutral-900 bg-neutral-50">
              <td
                colSpan={summaryColSpan}
                className="py-1.5 px-1.5 text-right font-bold text-neutral-900 uppercase tracking-wider text-[10px]"
              >
                {hasGST ? "Grand Total (Incl. Tax)" : "Grand Total"}
                {!isDeliveryIncluded && invoiceShipping <= 0 && (
                  <span className="block text-[8px] font-semibold text-amber-700 normal-case tracking-normal">
                    (Excl. shipping — will be calculated later)
                  </span>
                )}
              </td>
              <td className="py-1.5 px-1.5 text-right font-price text-base font-bold text-neutral-900">
                ₹{invoiceGrandTotal}
              </td>
            </tr>
            {isCodOrder && (
              <>
                <tr className="border-b border-neutral-200">
                  <td
                    colSpan={summaryColSpan}
                    className="py-1 px-1.5 text-right text-emerald-700 font-medium text-[10px]"
                  >
                    Online Advance Received (Prepaid)
                  </td>
                  <td className="py-1 px-1.5 text-right font-price font-semibold text-emerald-700">
                    − ₹{advancePaid}
                  </td>
                </tr>
                <tr className="border-b-2 border-neutral-900 bg-amber-50">
                  <td
                    colSpan={summaryColSpan}
                    className="py-1.5 px-1.5 text-right font-extrabold text-neutral-900 uppercase tracking-wider text-[11px]"
                  >
                    Balance Due on Delivery (Cash / UPI)
                  </td>
                  <td className="py-1.5 px-1.5 text-right font-price text-base font-extrabold text-terracotta">
                    ₹{balanceDue}
                  </td>
                </tr>
              </>
            )}
          </tfoot>
        </table>

        {/* Invoice Footer: Amount in Words, Declaration & Authorised Signatory */}
        <div className="mt-2 pt-1.5 border-t border-neutral-300 grid grid-cols-12 gap-3 items-end">
          {/* Left 7 cols: Amount in Words & Notes */}
          <div className="col-span-7 flex flex-col justify-between">
            <div className="bg-neutral-50 border border-neutral-200 p-2 rounded">
              <span className="text-[9px] uppercase tracking-wider font-extrabold text-neutral-500 block">
                Total Amount in Words:
              </span>
              <span className="font-bold text-neutral-900 text-xs font-serif italic">
                {numberToIndianWords(invoiceGrandTotal)}
              </span>
              {!isDeliveryIncluded && invoiceShipping <= 0 && (
                <span className="block text-[8.5px] text-amber-800 font-medium mt-0.5">
                  * Note: Shipping charge is not included and will be calculated later.
                </span>
              )}
            </div>

            {order.notes && (
              <div className="mt-1 text-[10px] text-neutral-600 italic">
                Note: {order.notes}
              </div>
            )}

            <div className="mt-1 text-[8.5px] text-neutral-500 leading-tight">
              Declaration: We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.
            </div>
          </div>

          {/* Right 5 cols: Signature Box */}
          <div className="col-span-5 text-right flex flex-col items-end justify-end">
            <div className="text-[10px] font-semibold text-neutral-700">
              For <strong className="text-neutral-900">{company.name}</strong>
            </div>

            {/* Signature PNG from /images/sig.png - Double Size */}
            <div className="h-[88px] w-64 flex items-center justify-end overflow-hidden my-1">
              <img
                src="/images/sig.png"
                alt="Signature"
                className="max-h-[88px] max-w-[260px] w-auto h-auto object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>

            <div className="border-t border-neutral-800 w-52 pt-0.5 text-center text-[9.5px] font-extrabold text-neutral-900 uppercase tracking-wider">
              Authorised Signatory
            </div>
          </div>
        </div>
      </section>

      {/* ─── Cut Line Divider ─────────────────────────────── */}
      <div className="cut-line my-2.5" aria-hidden>
        <Scissors className="w-3.5 h-3.5 text-neutral-500" />
        <span className="font-semibold text-[9px] tracking-[0.25em] text-neutral-500 uppercase">
          Cut Along Dotted Line For Shipping Label
        </span>
      </div>

      {/* ═══ BOTTOM HALF · WIDE HORIZONTAL SHIPPING LABEL ═════ */}
      <section className="pr-section shipping-label font-sans">
        <div className="border-2 border-neutral-900 rounded-lg overflow-hidden bg-white shadow-2xs">
          {/* Label Top Banner */}
          <div className="bg-neutral-900 text-white px-4 py-2 flex items-center justify-between text-xs font-bold uppercase tracking-wider">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-terracotta" />
              <span>Courier Shipping Label</span>
              {isCodOrder ? (
                <span className="ml-2 bg-amber-400 text-amber-950 font-black px-2 py-0.5 rounded text-[10px]">
                  COD: COLLECT ₹{balanceDue}
                </span>
              ) : (
                <span className="ml-2 bg-emerald-500 text-white font-black px-2 py-0.5 rounded text-[10px]">
                  PREPAID
                </span>
              )}
            </div>
            <div className="font-price font-semibold text-[11px] text-neutral-300 tracking-wider">
              Order #{String(order.id || "").toUpperCase()}
            </div>
          </div>

          {/* Horizontal Address Block (12 Columns) */}
          <div className="grid grid-cols-12 divide-x-2 divide-neutral-900 border-b-2 border-neutral-900">
            {/* FROM Address (Left 4 cols) */}
            <div className="col-span-4 p-3 bg-neutral-50/80 flex flex-col justify-between">
              <div>
                <div className="uppercase tracking-widest text-[9px] font-extrabold text-neutral-500 mb-1">
                  From (Sender)
                </div>
                <div className="font-sans font-extrabold text-sm text-neutral-900 leading-tight uppercase tracking-tight">
                  {company.senderName}
                </div>
                <div className="text-[11px] text-neutral-800 mt-1.5 leading-snug font-medium">
                  {company.address}
                  <br />
                  {company.city}
                  {company.state ? `, ${company.state}` : ""}
                  <br />
                  PIN CODE:{" "}
                  <strong className="text-neutral-900 font-extrabold font-price text-xs">
                    {company.pincode}
                  </strong>
                  <br />
                  Ph: <strong className="font-price font-bold">{company.phone}</strong>
                </div>
              </div>
            </div>

            {/* TO Address (Right 8 cols) */}
            <div className="col-span-8 p-3.5 bg-white flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="uppercase tracking-widest text-[10px] font-extrabold text-terracotta bg-terracotta/10 px-2 py-0.5 rounded">
                    To (Delivery Destination)
                  </span>
                </div>

                <div className="font-sans text-xl font-black text-neutral-900 leading-tight mt-1 uppercase tracking-tight">
                  {order.customerName}
                </div>

                <div className="text-xs text-neutral-900 font-semibold leading-relaxed mt-1.5 font-sans">
                  {order.address}
                  <br />
                  {order.city}
                  {order.state ? `, ${order.state}` : ""}
                </div>

                {/* PIN & Phone Highlight Bar */}
                <div className="mt-2.5 bg-neutral-100 border border-neutral-300 p-2 rounded flex items-center justify-between gap-4">
                  <div>
                    <span className="text-[9px] uppercase tracking-wider text-neutral-500 font-bold block">
                      Destination PIN Code
                    </span>
                    <span className="text-xl font-extrabold font-price text-neutral-900 tracking-widest">
                      {order.pincode}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-[9px] uppercase tracking-wider text-neutral-500 font-bold block">
                      Contact Phone
                    </span>
                    <span className="text-base font-bold font-price text-neutral-900 tracking-wide">
                      {order.phone}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Horizontal Spec Strip (4 Columns) */}
          <div className="grid grid-cols-4 divide-x-2 divide-neutral-900 bg-neutral-100 text-xs font-medium text-center">
            <div className="p-2">
              <span className="uppercase tracking-wider text-[9px] font-bold text-neutral-500 block">
                Items Qty
              </span>
              <span className="font-bold font-price text-neutral-900">
                {(order.items || []).reduce(
                  (s, i) => s + Number(i.quantity || 0),
                  0
                )}{" "}
                Pcs
              </span>
            </div>
            <div className={`p-2 ${isCodOrder ? "bg-amber-100/70" : ""}`}>
              <span className="uppercase tracking-wider text-[9px] font-bold text-neutral-600 block">
                {isCodOrder ? "Collect on Delivery (COD)" : "Payment Mode"}
              </span>
              {includeTotalValueOnLabel && !isCodOrder ? (
                <span className="font-extrabold font-price text-neutral-900">
                  ₹{invoiceGrandTotal}
                </span>
              ) : (
                <span className={`font-extrabold font-price ${isCodOrder ? "text-amber-950 text-sm" : "text-emerald-700"}`}>
                  {isCodOrder ? `₹${balanceDue}` : "PREPAID"}
                </span>
              )}
              {isCodOrder && (
                <span className="text-[8px] text-neutral-600 font-medium block">
                  (₹{advancePaid} advance paid)
                </span>
              )}
            </div>
            <div className="p-2">
              <span className="uppercase tracking-wider text-[9px] font-bold text-neutral-500 block">
                Carrier Partner
              </span>
              <span className="font-bold text-neutral-900">
                {order.courierName || "Others"}
              </span>
            </div>
            <div className="p-2 bg-neutral-200/80">
              <span className="uppercase tracking-wider text-[9px] font-bold text-neutral-600 block">
                Consignment / Tracking No.
              </span>
              <span className="font-price font-extrabold text-neutral-900 text-xs uppercase tracking-widest">
                {order.consignmentNumber || order.trackingNumber || "Awaiting"}
              </span>
            </div>
          </div>

          <div className="p-1.5 text-center text-[9.5px] text-neutral-600 bg-neutral-50 border-t border-neutral-300 font-medium">
            If undelivered, please return to sender at the <strong>FROM</strong> address on the left.
          </div>
        </div>
      </section>
    </div>
  );
});

export default InvoiceSheet;
